import type { ReactNode, SVGProps } from "react";

import { liveHomeCopy as enLiveHomeCopy } from "@/content/pages/en/live-home";
import { cn } from "@/lib/utils";

import { CardLede } from "./card-lede";
import type { TermsCopy } from "./types";

/** "No equity taken" — three ownership bars, the active two carrying an avatar
 * marker at the end of their fill. Track #F6F6F5, fills run light→dark. */
export interface EquityBarDefinition {
  name: string;
  value: number;
  trackClassName: string;
  fillClassName: string;
  marker?: ReactNode;
  muted?: boolean;
  /** Optional override for locale-specific display (defaults to `${value}%`). */
  displayPct?: string;
}

export const EQUITY_BARS: EquityBarDefinition[] = [
  {
    name: "Richard Wilson",
    value: 58.9,
    trackClassName: "h-3",
    fillClassName: "bg-gradient-to-r from-[#e6edff] to-[#5165ff]",
    marker: <PersonPlay />,
  },
  {
    name: "Jenny Black",
    value: 41.1,
    trackClassName: "h-2",
    fillClassName: "bg-gradient-to-r from-[#d9f0dd] to-[#93f3a4]",
    marker: <PersonCelebrate />,
  },
  {
    name: "Wayflyer",
    value: 0,
    trackClassName: "h-1",
    fillClassName: "",
    muted: true,
  },
];

const COMPACT_EQUITY_BARS: EquityBarDefinition[] = [
  {
    ...EQUITY_BARS[0]!,
    trackClassName: "h-[19px]",
    marker: <PersonPlay className="size-[25px]" />,
  },
  {
    ...EQUITY_BARS[1]!,
    trackClassName: "h-[12.5px]",
    marker: <PersonCelebrate className="size-[26px]" />,
  },
  {
    ...EQUITY_BARS[2]!,
    trackClassName: "h-[6px]",
  },
];

function formatBarPct(bar: EquityBarDefinition): string {
  return bar.displayPct ?? `${bar.value}%`;
}

function EquityBarRows({
  bars,
  labelClassName,
  rowGapClassName,
  markerSizeClassName,
}: {
  bars: EquityBarDefinition[];
  labelClassName?: string;
  rowGapClassName?: string;
  markerSizeClassName?: string;
}) {
  return (
    <>
      {bars.map((bar) => (
        <div key={bar.name} className={cn("flex flex-col", rowGapClassName)}>
          <div
            className={cn(
              "flex items-center justify-between leading-[1.2] tracking-[-0.04em]",
              labelClassName
            )}
          >
            <span className={bar.muted ? "text-grey-400" : "text-grey-600"}>
              {bar.name}
            </span>
            <span className={bar.muted ? "text-grey-400" : "text-grey-600"}>
              {formatBarPct(bar)}
            </span>
          </div>
          <div
            className={cn(
              "relative w-full rounded-full bg-[#f6f6f5]",
              bar.trackClassName
            )}
          >
            <div
              className={cn("relative h-full rounded-full", bar.fillClassName)}
              style={{ width: `${bar.value}%` }}
            >
              {bar.marker && (
                <span
                  className={cn(
                    "bg-grey-0 absolute top-1/2 right-0 flex translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full shadow-[0px_6.6px_23.6px_0px_rgba(0,0,0,0.16)]",
                    markerSizeClassName ?? "size-[27px]"
                  )}
                >
                  {bar.marker}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

export function EquityBars({ className }: { className?: string }) {
  return (
    <div className={cn("flex flex-col gap-[18px]", className)}>
      <EquityBarRows
        bars={EQUITY_BARS}
        labelClassName="text-[12px]"
        rowGapClassName="gap-2"
      />
    </div>
  );
}

/** Pre-Rive static equity card (Figma 5948:28607). Also reused on wholesale. */
export function StaticEquityCard({
  copy = enLiveHomeCopy.terms,
  compact = false,
}: {
  copy?: TermsCopy;
  compact?: boolean;
}) {
  const terms = copy;
  if (compact) {
    return (
      // Figma 8291:22577 — 566×371 card; content inset ~42px sides, ~54px top/bottom.
      <div
        data-testid="wholesale-equity-chart-card"
        className="border-grey-100 bg-grey-0 flex h-[371px] flex-col overflow-hidden rounded-2xl border px-[41px] pt-[53px] pb-[53px]"
      >
        <div className="flex flex-col gap-[26px]">
          <p className="text-grey-950 text-[20px] leading-[1.3] tracking-[-0.02em]">
            {terms.equityLead.replace(/\.$/, "")}
          </p>
          <div className="border-grey-100 border-t" />
          <div className="flex flex-col gap-[26px]">
            <EquityBarRows
              bars={COMPACT_EQUITY_BARS}
              labelClassName="text-[15.4px]"
              rowGapClassName="gap-3"
              markerSizeClassName="size-[42px] shadow-[0px_10.4px_37px_rgba(0,0,0,0.16)]"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    // Mobile (Figma 5948:28607): 24px padding; desktop 32px.
    <div className="border-grey-100 bg-grey-0 relative h-[280px] overflow-hidden rounded-2xl border p-6 lg:h-[280px] lg:p-8">
      <CardLede
        lead={terms.equityLead}
        rest={terms.equityRest}
        className="max-w-[267px] lg:max-w-[304px]"
      />

      <EquityBars className="absolute right-6 bottom-6 left-6 lg:right-8 lg:bottom-8 lg:left-8" />
    </div>
  );
}

/** Figma "Person play" marker (node 6142:29386), pulse-blue. */
export function PersonPlay(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className="size-4"
      {...props}
    >
      <g clipPath="url(#person-play-clip)">
        <path
          d="M3.66665 8.26699L1.06665 5.66699L3.66665 3.06699L6.26665 5.66699L3.66665 8.26699ZM5.99998 14.667V11.3337C5.32221 11.2781 4.64998 11.1975 3.98332 11.092C3.31665 10.9864 2.65554 10.8448 1.99998 10.667L2.33332 9.33366C3.26665 9.58922 4.20276 9.76421 5.14165 9.85866C6.08054 9.9531 7.03332 10.0003 7.99998 10.0003C8.96665 10.0003 9.91943 9.9531 10.8583 9.85866C11.7972 9.76421 12.7333 9.58922 13.6667 9.33366L14 10.667C13.3444 10.8448 12.6833 10.9864 12.0167 11.092C11.35 11.1975 10.6778 11.2781 9.99998 11.3337V14.667H5.99998ZM3.66665 6.40033L4.39998 5.66699L3.66665 4.93366L2.93332 5.66699L3.66665 6.40033ZM7.99998 4.66699C7.44443 4.66699 6.97221 4.47255 6.58332 4.08366C6.19443 3.69477 5.99998 3.22255 5.99998 2.66699C5.99998 2.11144 6.19443 1.63921 6.58332 1.25033C6.97221 0.861437 7.44443 0.666992 7.99998 0.666992C8.55554 0.666992 9.02776 0.861437 9.41665 1.25033C9.80554 1.63921 9.99998 2.11144 9.99998 2.66699C9.99998 3.22255 9.80554 3.69477 9.41665 4.08366C9.02776 4.47255 8.55554 4.66699 7.99998 4.66699ZM7.99998 9.33366C7.63332 9.33366 7.31943 9.2031 7.05832 8.94199C6.79721 8.68088 6.66665 8.36699 6.66665 8.00033C6.66665 7.63366 6.79721 7.31977 7.05832 7.05866C7.31943 6.79755 7.63332 6.66699 7.99998 6.66699C8.36665 6.66699 8.68054 6.79755 8.94165 7.05866C9.20276 7.31977 9.33332 7.63366 9.33332 8.00033C9.33332 8.36699 9.20276 8.68088 8.94165 8.94199C8.68054 9.2031 8.36665 9.33366 7.99998 9.33366ZM7.99998 3.33366C8.18887 3.33366 8.34721 3.26977 8.47498 3.14199C8.60276 3.01421 8.66665 2.85588 8.66665 2.66699C8.66665 2.4781 8.60276 2.31977 8.47498 2.19199C8.34721 2.06421 8.18887 2.00033 7.99998 2.00033C7.8111 2.00033 7.65276 2.06421 7.52498 2.19199C7.39721 2.31977 7.33332 2.4781 7.33332 2.66699C7.33332 2.85588 7.39721 3.01421 7.52498 3.14199C7.65276 3.26977 7.8111 3.33366 7.99998 3.33366ZM11.3667 8.00033L10.2333 6.00033L11.3667 4.00033H13.6333L14.7667 6.00033L13.6333 8.00033H11.3667ZM12.1333 6.66699H12.8667L13.2333 6.00033L12.8667 5.33366H12.1333L11.7667 6.00033L12.1333 6.66699Z"
          fill="#5486FA"
        />
      </g>
      <defs>
        <clipPath id="person-play-clip">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

/** Figma "Person celebrate" marker (node 6142:29390), green-500. */
export function PersonCelebrate(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 17 17"
      fill="none"
      aria-hidden
      className="size-[17px]"
      {...props}
    >
      <path
        d="M6.18693 13.748V10.9983C5.6026 10.9524 5.02973 10.8894 4.46831 10.8092C3.9069 10.729 3.34549 10.6259 2.78407 10.4999C2.5893 10.454 2.43462 10.3452 2.32005 10.1733C2.20547 10.0015 2.17683 9.81814 2.23412 9.62337C2.2914 9.42859 2.40884 9.28537 2.58643 9.19371C2.76402 9.10205 2.95021 9.07914 3.14498 9.12497C3.98137 9.31974 4.82636 9.4515 5.67994 9.52025C6.53351 9.58899 7.38996 9.62337 8.24926 9.62337C9.10857 9.62337 9.96501 9.58899 10.8186 9.52025C11.6722 9.4515 12.5172 9.31974 13.3535 9.12497C13.5598 9.07914 13.7488 9.10205 13.9207 9.19371C14.0925 9.28537 14.2071 9.42859 14.2644 9.62337C14.3217 9.81814 14.2902 10.0015 14.1699 10.1733C14.0496 10.3452 13.892 10.454 13.6973 10.4999C13.1359 10.6259 12.5744 10.729 12.013 10.8092C11.4516 10.8894 10.8845 10.9524 10.3116 10.9983V13.748C10.3116 13.9428 10.2457 14.1061 10.114 14.2378C9.9822 14.3696 9.81893 14.4355 9.62415 14.4355H6.87437C6.6796 14.4355 6.51633 14.3696 6.38457 14.2378C6.25281 14.1061 6.18693 13.9428 6.18693 13.748ZM8.24926 8.93592C7.85971 8.93592 7.53317 8.80416 7.26965 8.54064C7.00613 8.27712 6.87437 7.95058 6.87437 7.56103C6.87437 7.18294 7.00613 6.85926 7.26965 6.59001C7.53317 6.32076 7.85971 6.18614 8.24926 6.18614C8.62736 6.18614 8.95103 6.32076 9.22028 6.59001C9.48953 6.85926 9.62415 7.18294 9.62415 7.56103C9.62415 7.95058 9.48953 8.27712 9.22028 8.54064C8.95103 8.80416 8.62736 8.93592 8.24926 8.93592ZM3.09342 6.87358C2.79553 6.87358 2.5492 6.7762 2.35442 6.58142C2.15964 6.38664 2.06226 6.14031 2.06226 5.84242C2.06226 5.55598 2.15964 5.31251 2.35442 5.11201C2.5492 4.9115 2.79553 4.81125 3.09342 4.81125C3.37986 4.81125 3.62333 4.9115 3.82383 5.11201C4.02434 5.31251 4.12459 5.55598 4.12459 5.84242C4.12459 6.14031 4.02434 6.38664 3.82383 6.58142C3.62333 6.7762 3.37986 6.87358 3.09342 6.87358ZM13.4051 6.87358C13.1072 6.87358 12.8609 6.7762 12.6661 6.58142C12.4713 6.38664 12.3739 6.14031 12.3739 5.84242C12.3739 5.55598 12.4713 5.31251 12.6661 5.11201C12.8609 4.9115 13.1072 4.81125 13.4051 4.81125C13.6915 4.81125 13.935 4.9115 14.1355 5.11201C14.336 5.31251 14.4363 5.55598 14.4363 5.84242C14.4363 6.14031 14.336 6.38664 14.1355 6.58142C13.935 6.7762 13.6915 6.87358 13.4051 6.87358ZM4.9839 4.29567C4.686 4.29567 4.43967 4.19828 4.24489 4.0035C4.05012 3.80873 3.95273 3.56239 3.95273 3.2645C3.95273 2.97806 4.05012 2.73459 4.24489 2.53409C4.43967 2.33358 4.686 2.23333 4.9839 2.23333C5.27033 2.23333 5.5138 2.33358 5.71431 2.53409C5.91481 2.73459 6.01507 2.97806 6.01507 3.2645C6.01507 3.56239 5.91481 3.80873 5.71431 4.0035C5.5138 4.19828 5.27033 4.29567 4.9839 4.29567ZM11.5146 4.29567C11.2167 4.29567 10.9704 4.19828 10.7756 4.0035C10.5808 3.80873 10.4835 3.56239 10.4835 3.2645C10.4835 2.97806 10.5808 2.73459 10.7756 2.53409C10.9704 2.33358 11.2167 2.23333 11.5146 2.23333C11.8011 2.23333 12.0445 2.33358 12.245 2.53409C12.4455 2.73459 12.5458 2.97806 12.5458 3.2645C12.5458 3.56239 12.4455 3.80873 12.245 4.0035C12.0445 4.19828 11.8011 4.29567 11.5146 4.29567ZM8.24926 3.43636C7.95137 3.43636 7.70504 3.33897 7.51026 3.14419C7.31548 2.94942 7.21809 2.70308 7.21809 2.40519C7.21809 2.11876 7.31548 1.87529 7.51026 1.67478C7.70504 1.47428 7.95137 1.37402 8.24926 1.37402C8.5357 1.37402 8.77917 1.47428 8.97967 1.67478C9.18018 1.87529 9.28043 2.11876 9.28043 2.40519C9.28043 2.70308 9.18018 2.94942 8.97967 3.14419C8.77917 3.33897 8.5357 3.43636 8.24926 3.43636Z"
        fill="#5CD67A"
      />
    </svg>
  );
}
